// KycDecision widget — net-new for the admin chat surface (H.3.4a).
//
// Rendered inline when the admin gate intercepts a KycDecision write tool and
// emits a `:::widget[kyc-decision]{...}:::` directive (see admin-agent
// hooks-factory buildConfirmDirective). The card surfaces the KYC review
// context (investor/rmint/subscription IDs + optional profile snapshot, doc
// filenames, sub-agent finding) and TWO buttons — Approve / Reject — that
// round-trip through resumeFromDecision → POST /admin-agent/decision (Model
// Y). The IDs are carried verbatim so the decision route runs the confirmed
// tool deterministically.
//
// PD-4: calls useAdminChatContext() UNCONDITIONALLY → throws without a
// provider. Props arrive flat + camelCased from the MarkdownRenderer
// (ATTR_CAMELCASE_MAP); tool_use_id stays snake_case.

import { useState } from 'react';
import { useAdminChatContext } from '../../lib/admin-chat-context';

interface Props {
  tool_use_id?: string;
  sessionId?: string;
  subscriptionId?: string;
  investorId?: string;
  rmintId?: string;
  // Optional review context. May be base64-encoded JSON / text (the directive
  // layer can't carry braces/newlines safely) — decode best-effort.
  profileSnapshot?: string;
  kycDocFilenames?: string;
  subAgentFindingMarkdown?: string;
  decidedOverride?: string;
}

// Best-effort base64 decode. If the value isn't base64 (or decode fails) the
// raw string is returned unchanged so plain attrs still render.
function maybeDecode(v?: string): string | undefined {
  if (!v) return undefined;
  try {
    const decoded = atob(v);
    // atob succeeds on many non-base64 strings; only prefer the decode if it
    // round-trips back to the input (a cheap base64-ness check).
    if (btoa(decoded) === v) return decoded;
    return v;
  } catch {
    return v;
  }
}

export function KycDecision(p: Props) {
  const {
    tool_use_id,
    sessionId,
    subscriptionId,
    investorId,
    rmintId,
    decidedOverride,
  } = p;
  const profileSnapshot = maybeDecode(p.profileSnapshot);
  const kycDocFilenames = maybeDecode(p.kycDocFilenames);
  const subAgentFindingMarkdown = maybeDecode(p.subAgentFindingMarkdown);

  const [decided, setDecided] = useState<string | null>(decidedOverride ?? null);
  const [pending, setPending] = useState(false);
  const { resumeFromDecision } = useAdminChatContext();

  const click = async (action: 'approve' | 'reject') => {
    if (pending || decided) return;
    setPending(true);
    try {
      await resumeFromDecision({
        sessionId: sessionId ?? '',
        tool_use_id: tool_use_id ?? '',
        action,
        subscriptionId,
        investorId,
        rmintId,
      });
      setDecided(action);
    } catch (e) {
      console.warn('[kyc-decision] resume failed:', e);
    } finally {
      setPending(false);
    }
  };

  if (decided) {
    return (
      <div
        data-testid="kyc-decision"
        data-state="decided"
        className="my-2 rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground-mute"
      >
        KYC {decided === 'approve' ? 'approved' : 'rejected'}
        {investorId ? ` for ${investorId}` : ''}.
      </div>
    );
  }

  return (
    <div
      data-testid="kyc-decision"
      data-state="idle"
      className="my-2 rounded-md border border-border bg-card p-3 space-y-2"
    >
      <div className="text-sm font-semibold text-foreground">KYC review</div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
        <dt className="text-foreground-mute">Investor</dt>
        <dd className="text-foreground font-mono">{investorId ?? '—'}</dd>
        <dt className="text-foreground-mute">RMINT ID</dt>
        <dd className="text-foreground font-mono">{rmintId ?? '—'}</dd>
        <dt className="text-foreground-mute">Subscription</dt>
        <dd className="text-foreground font-mono">{subscriptionId ?? '—'}</dd>
      </dl>

      {profileSnapshot ? (
        <pre className="rounded bg-muted p-2 text-[11px] font-mono overflow-x-auto whitespace-pre-wrap text-foreground/80">
          {profileSnapshot}
        </pre>
      ) : null}

      {kycDocFilenames ? (
        <div className="text-xs text-foreground-mute">
          <span className="font-semibold">Docs:</span> {kycDocFilenames}
        </div>
      ) : null}

      {subAgentFindingMarkdown ? (
        <div className="rounded bg-muted p-2 text-xs text-foreground/80 whitespace-pre-wrap">
          {subAgentFindingMarkdown}
        </div>
      ) : null}

      <div className="mt-2 flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="kyc-approve"
          onClick={() => click('approve')}
          disabled={pending}
          className="rounded-md bg-primary px-3 py-1 text-sm text-primary-foreground disabled:cursor-not-allowed disabled:opacity-40"
        >
          Approve
        </button>
        <button
          type="button"
          data-testid="kyc-reject"
          onClick={() => click('reject')}
          disabled={pending}
          className="rounded-md border border-border px-3 py-1 text-sm text-foreground disabled:cursor-not-allowed disabled:opacity-40"
        >
          Reject
        </button>
      </div>
    </div>
  );
}
